import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import './ResetPassword.css';
import { API_URL } from '../global';

const LOGO = "https://res.cloudinary.com/dwrwkl5b3/image/upload/v1772788624/ChatGPT_Image_Mar_6__2026__02_43_41_PM-removebg-preview_wyghgu.png";

/* ── Inline SVG Icons ── */
const IconLock = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
    fill="none" stroke="#ff6a00" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const IconEye = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
    fill="none" stroke="#ff6a00" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const IconEyeOff = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
    fill="none" stroke="#ff6a00" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const IconReset = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
    fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 2v6h-6"/>
    <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
    <path d="M3 22v-6h6"/>
    <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
  </svg>
);

const IconBack = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
    fill="none" stroke="#0047cc" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const IconKeyShield = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 24 24"
    fill="none" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <defs>
      <linearGradient id="keyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0047cc"/>
        <stop offset="100%" stopColor="#ff6a00"/>
      </linearGradient>
    </defs>
    <path stroke="url(#keyGrad)" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    <circle stroke="url(#keyGrad)" cx="12" cy="11" r="2"/>
    <line stroke="url(#keyGrad)" x1="12" y1="13" x2="12" y2="16"/>
  </svg>
);

const ResetPassword = () => {
  const { id, token }           = useParams();
  const navigate                = useNavigate();
  const [showNew,  setShowNew]  = useState(false);
  const [showConf, setShowConf] = useState(false);
  const [headerH,  setHeaderH]  = useState(75);

  useEffect(() => {
    const measure = () => {
      const nav = document.querySelector('nav') || document.querySelector('header');
      if (nav) setHeaderH(nav.offsetHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const formik = useFormik({
    initialValues: { newPassword: '', confirmPassword: '' },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .min(4, 'Password must be at least 4 characters')
        .required('New Password is required'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
        .required('Confirm Password is required'),
    }),
    onSubmit: async (values) => {
      Swal.fire({
        title: 'Processing...',
        text: 'Please wait while we reset your password.',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      try {
        const response = await axios.post(`${API_URL}/resetpassword/${id}/${token}`, {
          password: values.newPassword,
        });
        if (response.data.statusCode === 200) {
          Swal.fire({
            icon: 'success',
            title: 'Password Reset!',
            text: response.data.message,
            timer: 2500,
            showConfirmButton: false,
          }).then(() => navigate('/lms/login'));
        } else {
          Swal.fire({ icon: 'error', title: 'Error', text: response.data.message });
        }
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'An error occurred while resetting the password. Please try again.',
        });
      }
    },
  });

  return (
    <div
      className="zuppa_lms_reset_pg_root"
      style={{ marginTop: `${headerH}px`, minHeight: `calc(100vh - ${headerH}px)` }}
    >
      {/* Background orbs */}
      <span className="zuppa_lms_reset_pg_orb zuppa_lms_reset_pg_orb_1" />
      <span className="zuppa_lms_reset_pg_orb zuppa_lms_reset_pg_orb_2" />
      <span className="zuppa_lms_reset_pg_orb zuppa_lms_reset_pg_orb_3" />

      {/* Card */}
      <div className="zuppa_lms_reset_pg_card">

        <div className="zuppa_lms_reset_pg_accent_bar" />

        <img src={LOGO} alt="Zuppa Logo" className="zuppa_lms_reset_pg_logo" />



        <h2 className="zuppa_lms_reset_pg_title">Reset Password</h2>
        <p className="zuppa_lms_reset_pg_sub">
          Create a strong new password for your Zuppa LMS account.
        </p>

        <form
          className="zuppa_lms_reset_pg_form"
          onSubmit={formik.handleSubmit}
          noValidate
        >
          {/* New Password */}
          <div className="zuppa_lms_reset_pg_field">
            <label className="zuppa_lms_reset_pg_label">New Password</label>
            <div className="zuppa_lms_reset_pg_input_wrap">
              <span className="zuppa_lms_reset_pg_input_icon"><IconLock /></span>
              <input
                type={showNew ? 'text' : 'password'}
                placeholder="Enter new password"
                className={`zuppa_lms_reset_pg_input${
                  formik.touched.newPassword && formik.errors.newPassword
                    ? ' zuppa_lms_reset_pg_input_bad' : ''
                }`}
                {...formik.getFieldProps('newPassword')}
              />
              <button
                type="button"
                className="zuppa_lms_reset_pg_eye"
                onClick={() => setShowNew(p => !p)}
                tabIndex={-1}
              >
                {showNew ? <IconEyeOff /> : <IconEye />}
              </button>
            </div>
            {formik.touched.newPassword && formik.errors.newPassword && (
              <p className="zuppa_lms_reset_pg_err">{formik.errors.newPassword}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div className="zuppa_lms_reset_pg_field">
            <label className="zuppa_lms_reset_pg_label">Confirm Password</label>
            <div className="zuppa_lms_reset_pg_input_wrap">
              <span className="zuppa_lms_reset_pg_input_icon"><IconLock /></span>
              <input
                type={showConf ? 'text' : 'password'}
                placeholder="Confirm your new password"
                className={`zuppa_lms_reset_pg_input${
                  formik.touched.confirmPassword && formik.errors.confirmPassword
                    ? ' zuppa_lms_reset_pg_input_bad' : ''
                }`}
                {...formik.getFieldProps('confirmPassword')}
              />
              <button
                type="button"
                className="zuppa_lms_reset_pg_eye"
                onClick={() => setShowConf(p => !p)}
                tabIndex={-1}
              >
                {showConf ? <IconEyeOff /> : <IconEye />}
              </button>
            </div>
            {formik.touched.confirmPassword && formik.errors.confirmPassword && (
              <p className="zuppa_lms_reset_pg_err">{formik.errors.confirmPassword}</p>
            )}
          </div>

          <button type="submit" className="zuppa_lms_reset_pg_btn">
            <IconReset /> Reset Password
          </button>
        </form>

        <button
          className="zuppa_lms_reset_pg_back_btn"
          onClick={() => navigate('/lms/login')}
        >
          <IconBack /> Back to Login
        </button>

      </div>
    </div>
  );
};

export default ResetPassword;